import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import * as d3 from 'd3';
import { ClusterFeatureSeries } from '../types';
import type { NumberValue } from 'd3';
import { ClusterReturnSeries } from '../calculation';

interface FeatureSeriesDataPoint {
    timestamp: Date;
    value: number;
}

interface PriceDataPoint {
    timestamp: Date;
    price: number;
}

interface GroupedFeatureSeries {
    featureName: string;
    values: FeatureSeriesDataPoint[];
}

interface FeatureLineChartProps {
    featureSeries: ClusterFeatureSeries;
    clusterReturnSeries: ClusterReturnSeries;
    selectedClusters: number[];
}

export const FeatureLineChart: React.FC<FeatureLineChartProps> = ({
    featureSeries,
    clusterReturnSeries,
    selectedClusters
}) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(800);
    const [hoverFeature, setHoverFeature] = useState<string | null>(null);

    // Responsive width
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Extract price data
    const priceData = useMemo<PriceDataPoint[]>(() => {
        return clusterReturnSeries.timeseriesData.map((d: any) => ({
            timestamp: new Date(d.timestamp),
            price: d.price
        })).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    }, [clusterReturnSeries]);

    // Price domain
    const priceDomain = useMemo(() => {
        const prices = priceData.map(d => d.price);
        return [Math.min(...prices), Math.max(...prices)] as [number, number];
    }, [priceData]);


    // Calculate min/max per feature for normalization
    const featureExtents = useMemo(() => {
        const extents = new Map<string, [number, number]>();

        featureSeries.allFeatureNames.forEach(featureName => {
            let values: number[] = [];
            featureSeries.timeseriesData.forEach(timePoint => {
                selectedClusters.forEach(clusterId => {
                    const value = timePoint.clusterFeatures[clusterId]?.[featureName];
                    if (value !== undefined && !isNaN(value)) {
                        values.push(value);
                    }
                });
            });

            if (values.length > 0) {
                extents.set(featureName, [Math.min(...values), Math.max(...values)]);
            }
        });

        return extents;
    }, [featureSeries, selectedClusters]);

    // Average data (normalized)
    const avgData = useMemo<GroupedFeatureSeries[]>(() => {
        const grouped = new Map<string, FeatureSeriesDataPoint[]>();

        featureSeries.timeseriesData.forEach(timePoint => {
            const timestamp = new Date(timePoint.timestamp);

            featureSeries.allFeatureNames.forEach(featureName => {
                const [min, max] = featureExtents.get(featureName) ?? [0, 1];
                const clusterValues: number[] = [];

                selectedClusters.forEach(clusterId => {
                    const value = timePoint.clusterFeatures[clusterId]?.[featureName];
                    if (value !== undefined && !isNaN(value)) {
                        const normalized =
                            max === min ? 0.5 : (value - min) / (max - min);
                        clusterValues.push(normalized);
                    }
                });

                if (clusterValues.length > 0) {
                    const averageValue =
                        clusterValues.reduce((sum, val) => sum + val, 0) /
                        clusterValues.length;

                    if (!grouped.has(featureName)) grouped.set(featureName, []);
                    grouped.get(featureName)!.push({ timestamp, value: averageValue });
                }
            });
        });

        grouped.forEach(arr =>
            arr.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
        );

        return Array.from(grouped.entries()).map(([featureName, values]) => ({
            featureName,
            values
        }));
    }, [featureSeries.timeseriesData, featureSeries.allFeatureNames, selectedClusters, featureExtents]);

    // Per-cluster data (normalized)
    const clusterData = useMemo(() => {
        const result = new Map<string, GroupedFeatureSeries[]>();

        featureSeries.allFeatureNames.forEach(featureName => {
            const [min, max] = featureExtents.get(featureName) ?? [0, 1];

            const perClusterSeries: GroupedFeatureSeries[] = selectedClusters.map(clusterId => {
                const values: FeatureSeriesDataPoint[] = [];
                featureSeries.timeseriesData.forEach(timePoint => {
                    const timestamp = new Date(timePoint.timestamp);
                    const value = timePoint.clusterFeatures[clusterId]?.[featureName];
                    if (value !== undefined && !isNaN(value)) {
                        const normalized =
                            max === min ? 0.5 : (value - min) / (max - min);
                        values.push({ timestamp, value: normalized });
                    }
                });
                values.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
                return {
                    featureName: `${featureName} (C${clusterId})`,
                    values
                };
            });

            result.set(featureName, perClusterSeries);
        });

        return result;
    }, [featureSeries, selectedClusters, featureExtents]);

    useEffect(() => {
        if (!svgRef.current || avgData.length === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 20, right: 50, bottom: 40, left: 50 };
        const height = 400 - margin.top - margin.bottom;
        const innerWidth = width - margin.left - margin.right;

        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        // Add mouseout handler to the entire chart area
        g.append('rect')
            .attr('width', innerWidth)
            .attr('height', height)
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all')
            .on('mouseout', (event) => {
                // Only clear if mouse is actually leaving the chart area
                const [mouseX, mouseY] = d3.pointer(event, g.node());
                if (mouseX < 0 || mouseX > innerWidth || mouseY < 0 || mouseY > height) {
                    setHoverFeature(null);
                }
            });

        const allValues = avgData.flatMap(d => d.values);

        const x = d3
            .scaleTime()
            .domain(d3.extent(allValues, d => d.timestamp) as [Date, Date])
            .range([0, innerWidth]);

        const yLeft = d3
            .scaleLinear()
            .domain(priceDomain)  // price domain now on left
            .nice()
            .range([height, 0]);

        const yRight = d3
            .scaleLinear()
            .domain([0, 1])  // normalized features now on right
            .nice()
            .range([height, 0]);

        const priceLine = d3
            .line<PriceDataPoint>()
            .x(d => x(d.timestamp))
            .y(d => yLeft(d.price))
            .curve(d3.curveMonotoneX);

        // Price line
        g.append('path')
            .datum(priceData)
            .attr('class', 'price-line')
            .attr('fill', 'none')
            .attr('stroke', theme.palette.text.primary)
            .attr('stroke-width', 2)
            .attr('stroke-opacity', hoverFeature && hoverFeature !== 'price' ? 0.1 : 1)
            .attr('d', priceLine)
            .on('mouseover', function () {
                svg.selectAll('.avg-line, .cluster-line')
                    .attr('opacity', 0.1);
                svg.selectAll('.price-line').attr('opacity', 1);
                setHoverFeature('price');
            })
            .on('mouseout', function () {
                svg.selectAll('.avg-line, .price-line, .cluster-line')
                    .attr('opacity', 1);
                setHoverFeature(null);
            });

        const line = d3
            .line<FeatureSeriesDataPoint>()
            .x(d => x(d.timestamp))
            .y(d => yRight(d.value));

        // Draw average lines
        g.selectAll('.avg-line')
            .data(avgData)
            .join('path')
            .attr('class', 'avg-line')
            .attr('fill', 'none')
            .attr('stroke', theme.palette.primary.main)
            .attr('stroke-width', 1.5)
            .attr('stroke-opacity', d =>
                hoverFeature && d.featureName !== hoverFeature ? 0.1 : 1
            )
            .attr('d', d => line(d.values) || '');

        // Invisible thick lines for easier hovering
        g.selectAll('.hover-line')
            .data(avgData)
            .join('path')
            .attr('class', 'hover-line')
            .attr('fill', 'none')
            .attr('stroke', 'transparent')
            .attr('stroke-width', 3) // Much thicker for easier hovering
            .attr('d', d => line(d.values) || '')
            .on('mouseover', (event, d) => {
                setHoverFeature(d.featureName);
            })
            .on('mouseout', (event) => {
                // Clear hover state when leaving a hover line and not immediately entering another
                setTimeout(() => {
                    const [mouseX, mouseY] = d3.pointer(event, g.node());
                    if (mouseX < 0 || mouseX > innerWidth || mouseY < 0 || mouseY > height) {
                        setHoverFeature(null);
                    }
                }, 50); // Small delay to prevent flickering between lines
            });

        // Per-cluster lines for hovered feature
        const clusterColorScale = d3.scaleOrdinal(d3.schemeCategory10);

        // Smooth line generator
        const smoothLine = d3
            .line<FeatureSeriesDataPoint>()
            .x(d => x(d.timestamp))
            .y(d => yRight(d.value))
            .curve(d3.curveMonotoneX); // Smooth interpolation

        if (hoverFeature) {

            g.append('text')
                .attr('class', 'feature-label')
                .attr('x', innerWidth - 10)
                .attr('y', 15)
                .attr('fill', theme.palette.primary.main)
                .attr('text-anchor', 'end')
                .style('font-size', '12px')
                .style('font-weight', 'bold')
                .text(hoverFeature);

            const perCluster = clusterData.get(hoverFeature) || [];

            // Draw cluster lines
            g.selectAll('.cluster-line')
                .data(perCluster)
                .join('path')
                .attr('class', 'cluster-line')
                .attr('fill', 'none')
                .attr('stroke', (d, i) => clusterColorScale(selectedClusters[i].toString()))
                .attr('stroke-width', 1.2)
                .attr('stroke-dasharray', '4 2')
                .attr('d', d => smoothLine(d.values) || '');

            // Add cluster labels at the start of each line
            g.selectAll('.cluster-label')
                .data(perCluster)
                .join('text')
                .attr('class', 'cluster-label')
                .attr('x', 5) // 5px from left edge
                .attr('y', (d, i) => {
                    if (d.values.length === 0) return 0;
                    const firstPoint = d.values[0];
                    return yRight(firstPoint.value) - 3; // Slightly above the line start
                })
                .attr('fill', (d, i) => clusterColorScale(selectedClusters[i].toString()))
                .attr('text-anchor', 'start')
                .style('font-size', '10px')
                .style('font-weight', 'bold')
                .text((d, i) => `Cluster ${selectedClusters[i]}`);
        }

        // Axes
        // X-axis
        const tickDates = x.ticks(10);
        let lastMonth = -1;
        let lastYear = -1;

        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(x)
                    .tickValues(tickDates)
                    .tickFormat((domainValue: Date | NumberValue, index: number) => {
                        const date = new Date(domainValue as Date);
                        const month = date.getMonth();
                        const year = date.getFullYear();
                        let label: string;

                        if (lastYear === -1) {
                            // First tick - show full date
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        } else if (year !== lastYear) {
                            label = `${year}`;
                        } else if (month !== lastMonth) {
                            label = d3.timeFormat('%B')(date);
                        } else {
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        }

                        lastMonth = month;
                        lastYear = year;
                        return label;
                    })
            );


        // Switch the axis calls
        g.append('g').call(d3.axisLeft(yLeft).tickFormat(d => `$${(d as number).toFixed(0)}`));  // price on left
        g.append('g')
            .attr('transform', `translate(${innerWidth}, 0)`)
            .call(d3.axisRight(yRight));  // features on right (no formatting needed since normalized 0-1)
    }, [avgData, clusterData, hoverFeature, selectedClusters, theme.palette.primary.main, width]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', overflowX: 'hidden' }}>
            <svg
                ref={svgRef}
                width={width}
                height={400}
                style={{ background: 'transparent' }}
            />
        </Box>
    );
};
